<div class="quota-block">
  <div class="quota-heading">
    <div>
      <h2 class="block-title">AI Agent · veckans förbrukning</h2>
      <p class="support-text">
        Gränsen gäller per företag och återställs på måndag.
      </p>
    </div>

    <span
      class="small-status"
      *ngIf="state?.limit"
      [class.off]="state?.blocked"
      [class.warn]="state?.warning && !state?.blocked"
      [class.on]="!state?.warning && !state?.blocked"
    >
      {{
        state?.blocked
          ? 'Gränsen nådd'
          : state?.warning
            ? 'Närmar sig gränsen'
            : 'Inom gränsen'
      }}
    </span>
  </div>

  <p class="quota-note" *ngIf="!supported">
    Den här servern känner inte till veckogränser ännu.
  </p>

  <div class="quota-loading" *ngIf="loading">
    <mat-spinner diameter="20"></mat-spinner>
    <span>Hämtar förbrukning …</span>
  </div>

  <p class="quota-error" *ngIf="loadError">
    {{ loadError }}
    <button mat-button type="button" (click)="load()">Försök igen</button>
  </p>

  <ng-container *ngIf="state && !loading && supported">
    <!--
      No budget set is a real state, not a missing value. Rendering $0 of $0
      here would read as "this company has no allowance" when it means the
      opposite: nothing is enforced for anyone yet.
    -->
    <p class="quota-note" *ngIf="!state.limit">
      Ingen veckogräns är satt. Inget blockeras förrän en gräns sparas under
      AI-inställningar.
    </p>

    <div class="quota-figures">
      <div class="quota-figure">
        <span class="quota-figure-label">Kostnad denna vecka</span>
        <strong class="quota-figure-value">{{ money(state.usage.costUsd) }}</strong>
        <span class="quota-figure-sub" *ngIf="state.limit as limit"
          >av {{ money(limit.weeklyCostLimitUsd) }}</span
        >
        <span class="quota-figure-sub" *ngIf="!state.limit">ingen gräns</span>
      </div>
      <div class="quota-figure">
        <span class="quota-figure-label">Meddelanden</span>
        <strong class="quota-figure-value">{{ state.usage.messageCount }}</strong>
        <span class="quota-figure-sub" *ngIf="previousUsage"
          >föregående vecka {{ previousUsage.messageCount }}</span
        >
      </div>
      <div class="quota-figure">
        <span class="quota-figure-label">Tokens</span>
        <strong class="quota-figure-value">{{
          tokens(state.usage.promptTokens + state.usage.completionTokens)
        }}</strong>
        <span class="quota-figure-sub" *ngIf="cacheHitPercent !== null"
          >{{ cacheHitPercent }} % ur cachen</span
        >
      </div>
    </div>

    <ng-container *ngIf="state.limit as limit">
      <div
        class="quota-bar"
        role="progressbar"
        [attr.aria-valuenow]="spentPercent"
        aria-valuemin="0"
        aria-valuemax="100"
        [attr.aria-label]="'Andel av veckans budget som är förbrukad'"
      >
        <div
          class="quota-bar-fill"
          [class.warn]="state.warning && !state.blocked"
          [class.over]="state.blocked"
          [style.width.%]="spentPercent"
        ></div>
      </div>

      <!--
        Where the budget comes from. Support's first question in front of a
        customer is whether this company is on the default or has already been
        given something, and a number alone cannot answer it.
      -->
      <p class="quota-source">
        <ng-container *ngIf="limit.source === 'global'"
          >Standardgränsen för alla företag.</ng-container
        >
        <ng-container *ngIf="limit.source === 'company'">
          Egen gräns för det här företaget<ng-container *ngIf="limit.validUntil"
            >, till och med
            {{ lastDayOf(limit.validUntil) | date: 'd MMM y' }}</ng-container
          ><ng-container *ngIf="!limit.validUntil"
            >, tills vidare</ng-container
          >.
          <span class="quota-source-reason" *ngIf="limit.reason">{{
            limit.reason
          }}</span>
        </ng-container>
      </p>

      <!--
        The figure above and the badge beside it will look like they disagree,
        because they answer different questions: the budget is not spent, and
        the chat is still shut. Support reads this card with the customer on the
        phone, so the sentence that reconciles them belongs here rather than in
        anyone's head.

        Hidden unless the backend said which of the two it is. An older one
        sends neither field, and a guess here would be a guess about whether a
        customer is locked out.
      -->
      <p
        class="quota-note"
        *ngIf="
          state.blocked &&
          state.budgetSpent === false &&
          state.minimumTurnCostUsd
        "
      >
        Gränsen är nådd trots att hela budgeten inte är förbrukad. Det som
        återstår är mindre än de {{ money(state.minimumTurnCostUsd) }} en tur
        måste reservera, så AI Agenten släpper inte igenom fler meddelanden
        förrän gränsen återställs på måndag. Höj gränsen för att öppna den nu.
      </p>

    </ng-container>

    <!--
      Spend the budget cannot see, so it is said out loud rather than folded
      into the figure above. A model with no price still costs money.
    -->
    <p class="quota-warning-note" *ngIf="state.usage.unpricedMessageCount">
      <mat-icon>warning</mat-icon>
      {{ state.usage.unpricedMessageCount }} av veckans svar kom från en
      modell utan prisuppgift. Kostnaden ovan är därför för låg.
    </p>

    <div class="quota-previous" *ngIf="previousUsage">
      Föregående vecka: {{ money(previousUsage.costUsd) }} ·
      {{ previousUsage.messageCount }}
      {{ previousUsage.messageCount === 1 ? 'meddelande' : 'meddelanden' }}
    </div>

    <div class="button-row" *ngIf="!raiseOpen">
      <button
        class="action-button primary"
        type="button"
        (click)="openRaise()"
        [disabled]="!state.limit"
      >
        Höj gränsen för det här företaget
      </button>
    </div>

    <div class="raise-form" *ngIf="raiseOpen">
      <mat-form-field appearance="outline" class="raise-amount">
        <mat-label>Ny veckogräns (USD)</mat-label>
        <input matInput type="number" min="0" step="0.5" [(ngModel)]="raiseAmount" />
      </mat-form-field>

      <mat-form-field appearance="outline" class="raise-reason">
        <mat-label>Varför höjs gränsen?</mat-label>
        <input
          matInput
          type="text"
          [(ngModel)]="raiseReason"
          placeholder="t.ex. momsvecka, kunden bokför hela kvartalet"
        />
      </mat-form-field>

      <!--
        Said before the click, not after. A raise granted on a Thursday lasts
        four days rather than seven, and that is the kind of thing people
        discover afterwards unless the form says it first.
      -->
      <p class="raise-hint">
        Gäller till och med söndag, alltså resten av innevarande vecka. Höjningen
        sparas med ditt namn och din motivering.
      </p>

      <p class="quota-error" *ngIf="raiseError">{{ raiseError }}</p>

      <div class="button-row">
        <button
          class="action-button primary"
          type="button"
          (click)="saveRaise()"
          [disabled]="raiseSaving"
        >
          {{ raiseSaving ? 'Sparar …' : 'Spara höjning' }}
        </button>
        <button
          class="action-button tertiary"
          type="button"
          (click)="cancelRaise()"
          [disabled]="raiseSaving"
        >
          Avbryt
        </button>
      </div>
    </div>

    <!--
      The point of an append-only table. Reading the pattern across companies
      is also the only honest way to find out what the default should have been.
    -->
    <div class="quota-history" *ngIf="state.history?.length">
      <h3>Tidigare höjningar</h3>
      <ul>
        <li *ngFor="let record of state.history">
          <strong>{{ money(record.weeklyCostLimitUsd) }}</strong>
          <span class="history-dates">
            {{ record.validFrom | date: 'd MMM y' }} –
            {{
              record.validUntil
                ? (lastDayOf(record.validUntil) | date: 'd MMM y')
                : 'tills vidare'
            }}
          </span>
          <span class="history-reason">{{ record.reason }}</span>
        </li>
      </ul>
    </div>
  </ng-container>
</div>
